import { useEffect, useState, useRef } from "react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Send, Plus, CheckCircle2, XCircle, Bot, User, Trash2, GraduationCap } from "lucide-react";

export default function TrainBot() {
  const [tab, setTab] = useState("simulator");
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [rules, setRules] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [fixDraft, setFixDraft] = useState({});
  const [fixOpen, setFixOpen] = useState({});
  const endRef = useRef(null);

  const loadSessions = () => api.get("/bot-training/sessions").then(({ data }) => setSessions(data));
  const loadRules = () => api.get("/bot-training/rules").then(({ data }) => setRules(data));

  useEffect(() => {
    loadSessions();
    loadRules();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages?.length]);

  const newSession = async () => {
    try {
      const { data } = await api.post("/bot-training/sessions");
      setSession(data);
      setFixOpen({});
      setFixDraft({});
      loadSessions();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openSession = async (id) => {
    const { data } = await api.get(`/bot-training/sessions/${id}`);
    setSession(data);
    setFixOpen({});
    setFixDraft({});
    setTab("simulator");
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    let sid = session?.id;
    if (!sid) {
      const { data } = await api.post("/bot-training/sessions");
      sid = data.id;
      setSession(data);
    }
    setSending(true);
    setInput("");
    try {
      const { data } = await api.post(`/bot-training/sessions/${sid}/message`, { text });
      setSession(data);
      loadSessions();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendFeedback = async (idx, verdict, correction) => {
    try {
      await api.post(`/bot-training/sessions/${session.id}/feedback`, {
        message_index: idx,
        verdict,
        correction: correction || "",
      });
      const { data } = await api.get(`/bot-training/sessions/${session.id}`);
      setSession(data);
      loadRules();
      loadSessions();
      if (verdict === "good") toast.success("Marked as good ✅");
      else toast.success("Correction saved — added as a learned rule");
      setFixOpen((s) => ({ ...s, [idx]: false }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveRule = async (rid, patch) => {
    try {
      await api.put(`/bot-training/rules/${rid}`, patch);
      loadRules();
      toast.success("Rule updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deleteRule = async (rid) => {
    if (!window.confirm("Remove this learned rule? The bot will no longer follow it.")) return;
    try {
      await api.delete(`/bot-training/rules/${rid}`);
      loadRules();
      toast.success("Rule removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const messages = session?.messages || [];

  return (
    <div className="space-y-6" data-testid="train-bot-page">
      <PageHead
        title="Train Bot"
        subtitle="Chat with the AI sales bot as a customer, rate its replies, and teach it new rules."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="train-bot-tabs">
          <TabsTrigger value="simulator" data-testid="tab-simulator">Simulator</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            Learned Rules{rules.length ? ` (${rules.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Session Log</TabsTrigger>
        </TabsList>

        {/* ---------- SIMULATOR ---------- */}
        <TabsContent value="simulator">
          <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
            <Card className="flex h-[560px] flex-col overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Bot className="h-4 w-4 text-crimson-500" />
                  {session ? (session.title || "Training session") : "New simulated chat"}
                </div>
                <Button size="sm" variant="outline" onClick={newSession} data-testid="new-session-btn">
                  <Plus className="mr-1 h-4 w-4" /> New chat
                </Button>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-window">
                {messages.length === 0 && (
                  <div className="mt-10 text-center text-sm text-slate-400">
                    Type a message as a customer to see how the bot replies.
                  </div>
                )}
                {messages.map((m, idx) => (
                  <div key={idx} className={m.role === "customer" ? "flex justify-end" : "flex justify-start"}>
                    <div className={`max-w-[85%] ${m.role === "customer" ? "" : "w-full"}`}>
                      <div
                        className={`whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                          m.role === "customer"
                            ? "bg-crimson-500 text-white"
                            : "border border-slate-200 bg-slate-50 text-slate-800"
                        }`}
                        data-testid={`msg-${m.role}-${idx}`}
                      >
                        <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold opacity-70">
                          {m.role === "customer" ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
                          {m.role === "customer" ? "You (as customer)" : "Neelima (bot)"}
                          {m.handoff && <Badge className="ml-1 border-0 bg-amber-100 text-amber-700">handoff</Badge>}
                          {m.booked && <Badge className="ml-1 border-0 bg-emerald-100 text-emerald-700">booked</Badge>}
                        </div>
                        {m.text}
                      </div>

                      {m.role === "bot" && m.generated !== false && (
                        <div className="mt-1.5 pl-1">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant={m.feedback === "good" ? "default" : "outline"}
                              className="h-7 text-xs"
                              data-testid={`good-btn-${idx}`}
                              onClick={() => sendFeedback(idx, "good")}
                            >
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Good response
                            </Button>
                            <Button
                              size="sm"
                              variant={m.feedback === "fix" ? "default" : "outline"}
                              className="h-7 text-xs"
                              data-testid={`fix-btn-${idx}`}
                              onClick={() => {
                                setFixOpen((s) => ({ ...s, [idx]: true }));
                                setFixDraft((s) => ({ ...s, [idx]: m.correction || "" }));
                              }}
                            >
                              <XCircle className="mr-1 h-3.5 w-3.5" /> Needs fix
                            </Button>
                            {m.feedback === "good" && <span className="text-xs text-emerald-600">✅ Good</span>}
                            {m.feedback === "fix" && !fixOpen[idx] && (
                              <span className="text-xs text-crimson-500">❌ Corrected</span>
                            )}
                          </div>

                          {(fixOpen[idx] || (m.feedback === "fix" && fixOpen[idx])) && (
                            <div className="mt-2 space-y-2">
                              <Textarea
                                rows={2}
                                placeholder="What should the bot have said instead?"
                                value={fixDraft[idx] ?? ""}
                                data-testid={`fix-input-${idx}`}
                                onChange={(e) => setFixDraft((s) => ({ ...s, [idx]: e.target.value }))}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs"
                                  data-testid={`save-fix-${idx}`}
                                  onClick={() => sendFeedback(idx, "fix", (fixDraft[idx] || "").trim())}
                                  disabled={!(fixDraft[idx] || "").trim()}
                                >
                                  Save correction
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs"
                                  onClick={() => setFixOpen((s) => ({ ...s, [idx]: false }))}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          {m.feedback === "fix" && m.correction && !fixOpen[idx] && (
                            <div className="mt-1 rounded-md bg-crimson-50 px-3 py-1.5 text-xs text-slate-600">
                              <span className="font-semibold">Correction:</span> {m.correction}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              <div className="flex items-center gap-2 border-t border-slate-100 p-3">
                <Input
                  placeholder="Type a customer message…"
                  value={input}
                  data-testid="chat-input"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  disabled={sending}
                />
                <Button onClick={send} disabled={sending || !input.trim()} data-testid="send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </Card>

            <Card className="hidden p-4 lg:block">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <GraduationCap className="h-4 w-4 text-crimson-500" /> How it works
              </div>
              <ol className="list-decimal space-y-2 pl-4 text-xs text-slate-500">
                <li>Type as a customer would — the bot replies exactly as it does on WhatsApp.</li>
                <li>Mark each reply <b>Good</b> or <b>Needs fix</b>.</li>
                <li>On a fix, type the ideal reply — it becomes a <b>learned rule</b> the bot follows for all customers.</li>
                <li>Manage learned rules under the <b>Learned Rules</b> tab.</li>
              </ol>
            </Card>
          </div>
        </TabsContent>

        {/* ---------- LEARNED RULES ---------- */}
        <TabsContent value="rules">
          <Card className="p-4" data-testid="rules-panel">
            {rules.length === 0 && (
              <div className="py-10 text-center text-sm text-slate-400">
                No learned rules yet. Mark a bot reply "Needs fix" in the Simulator to create one.
              </div>
            )}
            <div className="space-y-3">
              {rules.map((r) => (
                <RuleRow key={r.id} rule={r} onSave={saveRule} onDelete={deleteRule} />
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* ---------- SESSION LOG ---------- */}
        <TabsContent value="log">
          <Card className="p-4" data-testid="log-panel">
            {sessions.length === 0 && (
              <div className="py-10 text-center text-sm text-slate-400">No training sessions yet.</div>
            )}
            <div className="divide-y divide-slate-100">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50"
                  data-testid={`session-row-${s.id}`}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-800">{s.title || "Training session"}</div>
                    <div className="text-xs text-slate-400">{fmtDateTime(s.created_at)} · {s.message_count} messages</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.good_count > 0 && <Badge className="border-0 bg-emerald-100 text-emerald-700">✅ {s.good_count}</Badge>}
                    {s.fix_count > 0 && <Badge className="border-0 bg-crimson-50 text-crimson-500">❌ {s.fix_count}</Badge>}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RuleRow({ rule, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rule.rule_text || "");
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`rule-${rule.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {editing ? (
            <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid={`rule-edit-${rule.id}`} />
          ) : (
            <div className={`text-sm ${rule.active ? "text-slate-800" : "text-slate-400 line-through"}`}>
              {rule.rule_text}
            </div>
          )}
          <div className="mt-1 text-[11px] text-slate-400">
            {fmtDateTime(rule.created_at)} · by {rule.created_by_name || "Admin"}
            {!rule.active && " · inactive"}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {editing ? (
            <>
              <Button size="sm" className="h-7 text-xs" data-testid={`rule-save-${rule.id}`}
                onClick={() => { onSave(rule.id, { rule_text: text }); setEditing(false); }}>
                Save
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setText(rule.rule_text); setEditing(false); }}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`rule-toggle-${rule.id}`}
                onClick={() => onSave(rule.id, { active: !rule.active })}>
                {rule.active ? "Disable" : "Enable"}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`rule-editbtn-${rule.id}`}
                onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-crimson-500" data-testid={`rule-delete-${rule.id}`}
                onClick={() => onDelete(rule.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
